import { DIMENSION_KEYS, QUIZ_QUESTIONS } from './quizData';

export const QUIZ_STORAGE_KEY = 'precision_trader_quiz_state_v1';

/** Set when the user reaches quiz results (all questions answered); survives `clearQuizState` after email. */
export const QUIZ_COMPLETED_ONCE_KEY = 'precision_trader_quiz_completed_v1';

const SCHEMA_VERSION = 1;

function emptyScores() {
  return Object.fromEntries(DIMENSION_KEYS.map((k) => [k, 0]));
}

/**
 * @typedef {Object} QuizPersistedState
 * @property {number} schemaVersion
 * @property {number} contentFingerprint
 * @property {'segment'|'questions'|'results'} phase
 * @property {string | null} traderSegment — gating choice id; not scored
 * @property {number | null} seed
 * @property {number} questionIndex — current question index (0..n-1); equals QUIZ_QUESTIONS.length when finished
 * @property {Record<string, number>} scores
 * @property {Record<string, string>} answers — question id → selected option id
 */

const contentFingerprint = QUIZ_QUESTIONS.length;

/** Scores from answered questions with index ∈ [0, endExclusive). */
export function recomputeScoresFromAnswers(shuffledQuestions, answers, endExclusive) {
  const s = emptyScores();
  const end = Math.max(0, Math.min(endExclusive, shuffledQuestions.length));
  for (let i = 0; i < end; i += 1) {
    const q = shuffledQuestions[i];
    const optId = answers[q.id];
    if (!optId) continue;
    const opt = q.options.find((o) => o.id === optId);
    const delta = opt?.scores || {};
    for (const k of DIMENSION_KEYS) {
      s[k] += delta[k] || 0;
    }
  }
  return s;
}

/** Drop answers for questions at indices >= firstIndexToRemove. */
export function stripAnswersAfter(shuffledQuestions, answers, firstIndexToRemove) {
  const out = { ...answers };
  for (let i = firstIndexToRemove; i < shuffledQuestions.length; i += 1) {
    delete out[shuffledQuestions[i].id];
  }
  return out;
}

/** @returns {Omit<QuizPersistedState, 'schemaVersion'|'contentFingerprint'> | null} */
export function loadQuizState() {
  try {
    const raw = localStorage.getItem(QUIZ_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.schemaVersion !== SCHEMA_VERSION) return null;
    if (data.contentFingerprint !== contentFingerprint) return null;
    if (!['segment', 'questions', 'results'].includes(data.phase)) return null;
    if (data.phase === 'segment') {
      if (data.seed != null && typeof data.seed !== 'number') return null;
    }
    if (data.phase === 'questions') {
      if (typeof data.seed !== 'number') return null;
      if (data.questionIndex < 0 || data.questionIndex >= QUIZ_QUESTIONS.length) return null;
    }
    if (typeof data.questionIndex !== 'number' || data.questionIndex < 0) return null;
    if (!data.scores || typeof data.scores !== 'object') return null;
    const answers =
      data.answers && typeof data.answers === 'object' && !Array.isArray(data.answers)
        ? data.answers
        : {};
    const traderSegment =
      typeof data.traderSegment === 'string' && data.traderSegment.length > 0
        ? data.traderSegment
        : null;
    return {
      phase: data.phase,
      traderSegment,
      seed: data.seed ?? null,
      questionIndex: data.questionIndex,
      scores: data.scores,
      answers,
    };
  } catch {
    return null;
  }
}

/** @param {Omit<QuizPersistedState, 'schemaVersion'|'contentFingerprint'>} state */
export function persistQuizState(state) {
  const payload = {
    schemaVersion: SCHEMA_VERSION,
    contentFingerprint,
    ...state,
  };
  try {
    localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // quota / private mode
  }
}

export function clearQuizState() {
  try {
    localStorage.removeItem(QUIZ_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function markQuizCompletedOnce() {
  try {
    localStorage.setItem(QUIZ_COMPLETED_ONCE_KEY, '1');
  } catch {
    // ignore
  }
}

export function hasCompletedQuizOnce() {
  try {
    return localStorage.getItem(QUIZ_COMPLETED_ONCE_KEY) === '1';
  } catch {
    return false;
  }
}

export { emptyScores };
